import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";
import { applianceCategoryLabel } from "@/lib/applianceCategoryDisplay";

interface HarvestWorkflowHeaderProps {
  applianceId?: string | null;
  workOrderId?: string | null;
  brand?: string | null;
  model?: string | null;
  serial?: string | null;
  category?: string | null;
  currentStep: 1 | 2 | 3 | 4 | 5;
  stepTitle?: string;
  stepHint?: React.ReactNode;
  headerActions?: React.ReactNode;
  applianceTrailing?: React.ReactNode;
}

function workOrderShortId(uuid?: string | null): string {
  if (!uuid) return "—";
  return `FIXID-${uuid.replace(/-/g, "").slice(0, 8).toUpperCase()}`;
}

export function HarvestWorkflowHeader({
  applianceId,
  workOrderId,
  brand,
  model,
  serial,
  category,
  currentStep,
  stepTitle,
  stepHint,
  headerActions,
  applianceTrailing,
}: HarvestWorkflowHeaderProps) {
  const { t } = useTranslation("harvest");

  const stepKeys = ["select", "extract", "print", "bins", "complete"] as const;
  const steps = stepKeys.map((key, idx) => ({
    num: (idx + 1) as 1 | 2 | 3 | 4 | 5,
    label: t(`process.steps.${key}`),
  }));

  const crumbLeftParts = [brand, model].filter(Boolean) as string[];
  const crumbLeft = crumbLeftParts.length > 0 ? crumbLeftParts.join(" · ") : t("process.applianceFallback");
  const woId = workOrderShortId(workOrderId);

  const categoryLabel = applianceCategoryLabel({ category }, t("process.categoryOther"));
  const serialSuffix = serial && serial.length > 0 ? serial : null;
  const titleParts = [brand, model].filter(Boolean).join(" · ");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6 space-y-3 sm:space-y-4 print:hidden">
      {/* 1. Breadcrumbs */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={applianceId ? `/appliance/${applianceId}` : "/work-orders"}>{crumbLeft}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              <span className="hidden sm:inline">{t("process.workOrderLabel")}: </span>
              <span className="font-mono">{woId}</span>
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* 2. Progress strip */}
      <div className="bg-card rounded-2xl border border-border shadow-sm p-3 sm:p-4">
        <ol className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto">
          {steps.map((s, i) => {
            const state =
              s.num < currentStep ? "done" : s.num === currentStep ? "active" : "todo";
            return (
              <li key={s.num} className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                {state === "active" ? (
                  <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 whitespace-nowrap">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
                      {s.num}
                    </span>
                    <span className="text-sm font-semibold text-primary">{s.label}</span>
                  </div>
                ) : state === "done" ? (
                  <div className="inline-flex items-center gap-2 px-1 py-1.5 whitespace-nowrap">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-harvest/15 text-harvest shrink-0">
                      <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm text-harvest">{s.label}</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 px-1 py-1.5 whitespace-nowrap">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-muted-foreground/30 text-muted-foreground text-xs font-medium shrink-0">
                      {s.num}
                    </span>
                    <span className="text-sm text-muted-foreground">{s.label}</span>
                  </div>
                )}
                {i < steps.length - 1 && (
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* 3. Appliance bar */}
      <div className="bg-card rounded-2xl border border-border shadow-sm p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {categoryLabel} · {t("process.harvestingEyebrow")}
            </p>
            <p className="mt-1 break-words">
              <span className="text-base font-semibold text-foreground">
                {titleParts || t("process.applianceFallback")}
              </span>
              {serialSuffix && (
                <>
                  <span className="text-sm font-normal text-muted-foreground"> · </span>
                  <span className="text-sm font-normal text-muted-foreground font-mono">{serialSuffix}</span>
                </>
              )}
            </p>
          </div>
          {applianceTrailing && (
            <div className="shrink-0 text-right">{applianceTrailing}</div>
          )}
        </div>
        {(stepTitle || stepHint || headerActions) && (
          <div className="mt-3 pt-3 border-t border-border">
            {stepTitle && (
              <div className="flex items-center gap-2">
                {currentStep === 5 && (
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-harvest text-white shrink-0">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </span>
                )}
                <p className="text-lg font-semibold text-foreground">{stepTitle}</p>
              </div>
            )}
            {stepHint && (
              <p className="text-sm text-muted-foreground mt-0.5">{stepHint}</p>
            )}
            {headerActions && (
              <div className="mt-3">{headerActions}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
